import React from 'react';

const PermissionGrid = ({ allPermissions, selectedPermissions, onToggle, onToggleAll }) => {
  const allSelected = selectedPermissions.length === allPermissions.length;

  // Group permissions by category with subcategories
  const groupedPermissions = {
    'Dashboard': {
      'General': allPermissions.filter(p => p.includes('Dashboard'))
    },
    'Users Management': {
      'Basic Operations': allPermissions.filter(p =>
        p.includes('User') &&
        (p.includes('View') || p.includes('Create') || p.includes('Update') || p.includes('Delete'))
      ),
      'Advanced Operations': allPermissions.filter(p =>
        p.includes('User') &&
        !p.includes('View') &&
        !p.includes('Create') &&
        !p.includes('Update') &&
        !p.includes('Delete')
      )
    },
    'Groups Management': {
      'Basic Operations': allPermissions.filter(p =>
        p.includes('Group') &&
        (p.includes('View') || p.includes('Create') || p.includes('Update') || p.includes('Delete'))
      ),
      'Advanced Operations': allPermissions.filter(p =>
        p.includes('Group') &&
        !p.includes('View') &&
        !p.includes('Create') &&
        !p.includes('Update') &&
        !p.includes('Delete')
      )
    },
    'Invoices': {
      'Basic Operations': allPermissions.filter(p =>
        p.includes('Invoice') &&
        (p.includes('View') || p.includes('Create') || p.includes('Update') || p.includes('Delete'))
      )
    },
    'Forms Management': {
      'Basic Operations': allPermissions.filter(p =>
        p.includes('Form') &&
        !p.includes('Submission') &&
        (p.includes('View') || p.includes('Create') || p.includes('Update') || p.includes('Delete'))
      )
    },
    'Form Submissions': {
      'Basic Operations': allPermissions.filter(p =>
        p.includes('Form Submission') &&
        (p.includes('View') || p.includes('Create') || p.includes('Update') || p.includes('Delete'))
      )
    },
    'Deadlines': {
      'Basic Operations': allPermissions.filter(p =>
        p.includes('Deadlines') &&
        (p.includes('View') || p.includes('Create') || p.includes('Update') || p.includes('Delete'))
      )
    },
    'Master Data': {
      'Basic Operations': allPermissions.filter(p =>
        p.includes('Master Data') &&
        (p.includes('View') || p.includes('Create') || p.includes('Update') || p.includes('Delete'))
      )
    },
    'Tasks': {
      'Basic Operations': allPermissions.filter(p =>
        (p.endsWith('Task') || p.endsWith('Tasks')) &&
        (p.includes('View') || p.includes('Create') || p.includes('Update') || p.includes('Delete'))
      ),
      'Task Interactions': allPermissions.filter(p =>
        p.includes('Task') &&
        (p.includes('Comment') || p.includes('Attachment') || p.includes('History') || p.includes('Status'))
      )
    },
    'Time Log Management': {
      'Basic Operations': allPermissions.filter(p =>
        p.includes('Time Log') &&
        (p.includes('View') || p.includes('Create') || p.includes('Update') || p.includes('Delete'))
      )
    },
    'India Post & Court Cases': {
      'Access': allPermissions.filter(p => p.includes('India Post') || p.includes('Court Cases'))
    },
    'Checklist': {
      'Basic Operations': allPermissions.filter(p =>
        p.includes('Checklist') &&
        (p.includes('View') || p.includes('Create') || p.includes('Update') || p.includes('Delete'))
      ),
    },
    'File Uploads': {
      'File Operations': allPermissions.filter(p => 
        p.startsWith('Upload Files')
      )
    },
    'Trademark': {
      'Basic Operations': allPermissions.filter(p => p.includes('Trademark')),
    },
    'Payments': {
      'Basic Operations': allPermissions.filter(p => p.includes('Payment')),
    },
    'Document Templates': {
      'Basic Operations': allPermissions.filter(p => p.includes('Document Template')),
    },
    'Admin Settings': { 
      'Form Settings': allPermissions.filter(p => p.includes('Form Settings')),
      'Client Types': allPermissions.filter(p => p.includes('Client Type')),
      'Products': allPermissions.filter(p => p.includes('Product')),
      'Upload Fields': allPermissions.filter(p => p.includes('Upload Field')),
      'Verification Types': allPermissions.filter(p => p.includes('Verification Type')),
      'Email Gateways': allPermissions.filter(p => p.includes('Email Gateway')),
      'Invoice Settings': allPermissions.filter(p => p.includes('Invoice Settings')),
      'Email Templates': allPermissions.filter(p => p.includes('Email Template')),
      'Form Categories': allPermissions.filter(p => p.includes('Form Categor')),
      'Custom Pages': allPermissions.filter(p => p.includes('Custom Page')),
      'Custom Page Documents': allPermissions.filter(p => p.includes('Custom Page Documents')),
      'Blocks': allPermissions.filter(p => p.includes('Block'))
    }
  };

  const toggleCategory = (category) => {
    const categoryPermissions = [];
    Object.values(groupedPermissions[category]).forEach(subcategory => {
      categoryPermissions.push(...subcategory);
    });

    const allCategorySelected = categoryPermissions.every(p => selectedPermissions.includes(p));

    if (allCategorySelected) {
      // Remove all permissions in this category
      const newPermissions = selectedPermissions.filter(p => !categoryPermissions.includes(p));
      onToggle(null, newPermissions);
    } else {
      // Add all permissions in this category
      const newPermissions = [...selectedPermissions];
      categoryPermissions.forEach(p => {
        if (!newPermissions.includes(p)) {
          newPermissions.push(p);
        }
      });
      onToggle(null, newPermissions);
    }
  };

  const toggleSubcategory = (category, subcategory) => {
    const subcategoryPermissions = groupedPermissions[category][subcategory];
    const allSubcategorySelected = subcategoryPermissions.every(p => selectedPermissions.includes(p));

    if (allSubcategorySelected) {
      // Remove all permissions in this subcategory
      const newPermissions = selectedPermissions.filter(p => !subcategoryPermissions.includes(p));
      onToggle(null, newPermissions);
    } else {
      // Add all permissions in this subcategory
      const newPermissions = [...selectedPermissions];
      subcategoryPermissions.forEach(p => {
        if (!newPermissions.includes(p)) {
          newPermissions.push(p);
        }
      });
      onToggle(null, newPermissions);
    }
  };

  const isCategorySelected = (category) => {
    const categoryPermissions = [];
    Object.values(groupedPermissions[category]).forEach(subcategory => {
      categoryPermissions.push(...subcategory);
    });
    return categoryPermissions.length > 0 && categoryPermissions.every(p => selectedPermissions.includes(p));
  };

  const isCategoryPartiallySelected = (category) => {
    const categoryPermissions = [];
    Object.values(groupedPermissions[category]).forEach(subcategory => {
      categoryPermissions.push(...subcategory);
    });
    const selectedCount = categoryPermissions.filter(p => selectedPermissions.includes(p)).length;
    return selectedCount > 0 && selectedCount < categoryPermissions.length;
  };

  const isSubcategorySelected = (category, subcategory) => {
    const subcategoryPermissions = groupedPermissions[category][subcategory];
    return subcategoryPermissions.length > 0 && subcategoryPermissions.every(p => selectedPermissions.includes(p));
  };

  const isSubcategoryPartiallySelected = (category, subcategory) => {
    const subcategoryPermissions = groupedPermissions[category][subcategory];
    const selectedCount = subcategoryPermissions.filter(p => selectedPermissions.includes(p)).length;
    return selectedCount > 0 && selectedCount < subcategoryPermissions.length;
  };

  return (
    <div className="space-y-6">
      {/* Allow All Checkbox */}
      <div className="border-b pb-4">
        <label className="flex items-center text-lg font-semibold">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={onToggleAll}
            className="mr-3 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          Allow All Permissions
        </label>
      </div>

      {/* Permission Categories */}
      <div className="space-y-4">
        {Object.entries(groupedPermissions).map(([category, subcategories]) => {
          const categoryPermissions = [];
          Object.values(subcategories).forEach(subcategory => {
            categoryPermissions.push(...subcategory);
          });

          if (categoryPermissions.length === 0) return null;

          return (
            <div key={category} className="border rounded-lg p-4">
              <div className="mb-3">
                <label className="flex items-center font-medium text-gray-900">
                  <input
                    type="checkbox"
                    checked={isCategorySelected(category)}
                    ref={(input) => {
                      if (input) {
                        input.indeterminate = isCategoryPartiallySelected(category);
                      }
                    }}
                    onChange={() => toggleCategory(category)}
                    className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="font-semibold">{category}</span>
                </label>
              </div>

              <div className="space-y-3 ml-6">
                {Object.entries(subcategories).map(([subcategory, permissions]) => {
                  if (permissions.length === 0) return null;

                  return (
                    <div key={subcategory} className="border-l-2 border-gray-200 pl-4">
                      <div className="mb-2">
                        <label className="flex items-center text-sm font-medium text-gray-700">
                          <input
                            type="checkbox"
                            checked={isSubcategorySelected(category, subcategory)}
                            ref={(input) => {
                              if (input) {
                                input.indeterminate = isSubcategoryPartiallySelected(category, subcategory);
                              }
                            }}
                            onChange={() => toggleSubcategory(category, subcategory)}
                            className="mr-2 h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          {subcategory}
                        </label>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 ml-4">
                        {permissions.map((perm) => (
                          <label key={perm} className="flex items-center text-xs">
                            <input
                              type="checkbox"
                              checked={selectedPermissions.includes(perm)}
                              onChange={() => onToggle(perm)}
                              className="mr-2 h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-gray-600">{perm}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermissionGrid; 